
import React, { useState, useEffect } from 'react';
import { translations, Language } from '../translations';
import { ShieldAlert, CheckCircle2, AlertTriangle, Scale, Lock, XCircle } from 'lucide-react';

interface Props {
  lang: Language;
  onAccept: () => void;
  onReject: () => void;
}

const DisclaimerModal: React.FC<Props> = ({ lang, onAccept, onReject }) => {
  const [isVisible, setIsVisible] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastAcceptedDate = localStorage.getItem('id_pro_legal_accepted_date');
    if (lastAcceptedDate !== today) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('id_pro_legal_accepted_date', today);
    setIsVisible(false);
    onAccept();
  };

  const handleReject = () => {
    onReject();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-labelledby="disclaimer-title">
      <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl" />
      <div className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-500 border border-slate-100">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 via-red-600 to-orange-500"></div>
        
        <div className="p-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-orange-100 text-orange-700 rounded-2xl flex items-center justify-center shadow-sm">
              <ShieldAlert size={32} />
            </div>
            <div>
              <h2 id="disclaimer-title" className="text-2xl font-black text-slate-900 tracking-tight">{t.legalTitle}</h2>
              <p className="text-xs text-slate-800 font-bold uppercase tracking-widest mt-1">Daily Legal Consent Required</p>
            </div>
          </div>

          <div className="space-y-6 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar text-slate-800 text-sm leading-relaxed">
             <div className="p-4 bg-orange-100/50 border border-orange-200 rounded-2xl">
                <p className="font-bold text-orange-950">⚠️ 系统检测到敏感功能。您需要阅读并同意以下免责声明后方可访问本站。</p>
             </div>
             <p>1. <span className="font-bold text-slate-950">数据性质：</span>本站所有数据均由 AI 算法随机合成，仅供技术研究、压力测试或 UI 占位使用。</p>
             <p>2. <span className="font-bold text-slate-950">禁止行为：</span>严禁利用本系统生成的任何数据从事非法活动，包括但不限于欺诈、冒用身份或绕过实名认证。</p>
             <p>3. <span className="font-bold text-slate-950">责任归属：</span>使用者需对自身所有操作行为承担全部法律责任。如果您不同意，请立即退出。</p>
          </div>

          <div className="mt-10 pt-8 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button 
              type="button"
              onClick={handleReject}
              aria-label={t.rejectDisclaimer}
              className="w-full py-4 bg-slate-200 text-slate-900 rounded-2xl font-bold text-sm flex items-center justify-center gap-3 hover:bg-red-200 hover:text-red-900 transition-all border border-slate-300"
            >
              <XCircle size={20} /> {t.rejectDisclaimer}
            </button>
            <button 
              type="button"
              onClick={handleAccept}
              aria-label={t.confirmDisclaimer}
              className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-3 hover:bg-blue-800 transition-all shadow-xl"
            >
              <CheckCircle2 size={20} className="text-green-400" /> {t.confirmDisclaimer}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
